import React from "react";
import { Link } from "react-router-dom";
import { getLeagueStatus } from "./leagueStatus";
import { completedOnLabel, leagueTypeKey } from "./leagueFormat";
import useMinuteTick from "./useMinuteTick";

// The leaderboard's own status line — the one that sits directly beneath the
// league title on a standalone leaderboard.
//
// Client, the original spec:
//
//   Add the status line directly beneath the league title.
//   Live format:      (the green live dot) Live now · Ends in 3h
//   Completed format: Completed · Sep 12, 2026
//   Keep Verified league performance unchanged for both states.
//
// and then, this round, a third segment on BOTH states and a split by membership:
//
//   "For the completed state and live state, we added a status line under the
//    header for the leaderboard preview screen. We need to add to this status
//    line. 'Game details' will take the user to the game details preview screen
//    so they can see what the competition involves...
//
//      Live · Ends in Xh · Game details ›
//      Completed · [date] · Game details ›
//
//    For members
//      Live · Ends in Xh · League Home ›
//      Completed · [date] · League Home ›"
//
// and then, once the client saw it, the member segment was renamed and split the
// same way the visitor one already was:
//
//   "cash games:
//      Live · Ends in Xh · Game details › / Completed · [date] · Game details › (non-members)
//      Live · Ends in Xh · Game › / Completed · [date] · Game › (members)
//    League's:
//      Live · Ends in Xh · League details › / Completed · [date] · League details › (non-members)
//      Live · Ends in Xh · League › / Completed · [date] · League › (members)"
//
// So the trailing segment is now one word plus a chevron for a member — "Game ›"
// on a cash game, "League ›" on a league — and it still goes to the same place
// "League Home ›" did: the competition's home screen inside the shell.
//
// Three things changed from the first version, all of them written out by the
// client above:
//
//   • the live word is "Live", not "Live now"
//   • a LIVE league now carries the third segment too; before, only Completed did
//   • a non-member gets "Game details ›" where a member gets "League Home ›" —
//     previously a non-member got nothing, because the only destination on offer
//     was the league interior, which they cannot enter
//
// The green dot stays: it was an explicit instruction in the earlier spec ("the
// green live dot") and this round's note is about what the line SAYS, not about
// removing it.
//
// Only the last segment is ever a link. The state and the date are statements,
// and making the whole line tappable would invite a click on "Completed".
const TEAL = "#0F4146";

const LeagueResultStatusLine = ({
  game,
  completed,
  // Membership decides the destination, not the wording of the rest of the line.
  // The caller passes it because getGameFromSlug is the only thing that knows.
  isMember = false,
  className = "",
}) => {
  const status = getLeagueStatus(game);
  // Keeps "Ends in 59m" honest while someone sits on the board watching the last
  // hour of a league — which is exactly when they will be watching it.
  useMinuteTick(!completed);

  if (!game) return null;

  const slug = game?.vanity_url;
  // "Game details" for a cash game, "League details" for a league — the client
  // wrote the cash-game wording because they were looking at a cash game, and
  // League Play already names the two kinds of competition differently
  // everywhere else (leagueTypeLabel). Calling a private league's preview "Game
  // details" would be the only place on the site that does.
  const isGame = leagueTypeKey(game) === "cash";
  const trailing = isMember ? (
    <Link
      to={`/contest/${slug}`}
      className="font-semibold hover:underline"
      style={{ color: TEAL }}
    >
      {isGame ? "Game ›" : "League ›"}
    </Link>
  ) : (
    <Link
      to={`/leaderboards/${slug}/details`}
      className="font-semibold hover:underline"
      style={{ color: TEAL }}
    >
      {isGame ? "Game details ›" : "League details ›"}
    </Link>
  );

  if (completed) {
    return (
      <p className={`text-sm text-tertiary ${className}`}>
        {completedOnLabel(game)}
        {slug && (
          <>
            {" · "}
            {trailing}
          </>
        )}
      </p>
    );
  }

  const endsIn = status?.countdownValue;

  return (
    <p className={`flex flex-wrap items-center gap-x-1.5 text-sm ${className}`}>
      {/* The live dot. Its own element rather than a bullet glyph, so it keeps
          its size and colour independently of the type around it. */}
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 shrink-0 rounded-full bg-[#35B23C]"
      />
      <span className="font-semibold text-[#35B23C]">Live</span>
      {endsIn && <span className="text-tertiary">· Ends in {endsIn}</span>}
      {slug && (
        <span className="text-tertiary">
          {"· "}
          {trailing}
        </span>
      )}
    </p>
  );
};

export default LeagueResultStatusLine;
